import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
  Observable,
  from,
  split,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';

import { getAccessToken, logout, refreshAccessToken } from './auth';
import { SSELink } from './sseLink';

const graphqlUri =
  (import.meta.env.VITE_GRAPHQL_URL as string | undefined) ?? '/graphql';

// Attach the bearer access token to every request.
const authLink = new ApolloLink((operation, forward) => {
  const token = getAccessToken();
  if (token) {
    // Stamp the token this request used so the error link can tell a request
    // that failed with a stale token from one that failed with the current one.
    operation.setContext(({ headers = {} }) => ({
      headers: { ...headers, authorization: `Bearer ${token}` },
      ferryAccessToken: token,
    }));
  }
  return forward(operation);
});

function isUnauthenticated(
  errors: readonly { extensions?: Record<string, unknown> }[] | undefined,
): boolean {
  return !!errors?.some((e) => e?.extensions?.code === 'UNAUTHENTICATED');
}

// Go answers an expired access token with HTTP 200 and a GraphQL error
// `extensions.code = UNAUTHENTICATED` (on `liveUpdated` it sends one such frame
// and completes). Apollo 4 delivers that to ErrorLink as CombinedGraphQLErrors.
// Refresh once (parallel failures share one refresh) and retry; `authLink`
// (HTTP) and graphql-sse `headers()` (SSE) re-read the new token on the retry.
// A dead refresh session logs out; a refresh that fails on 5xx / network keeps
// the session and surfaces the original error. A retry that is UNAUTHENTICATED
// again logs out instead of refreshing again. A request that was sent with a
// token that another operation has since replaced skips the refresh (a second
// one would rotate the cookie again) and goes straight to its single retry.
const errorLink = new ErrorLink(({ error, operation, forward }) => {
  if (!CombinedGraphQLErrors.is(error) || !isUnauthenticated(error.errors)) {
    console.error(
      '[graphql] operation failed:',
      operation.operationName,
      error,
    );
    return undefined;
  }

  const usedToken = operation.getContext().ferryAccessToken as
    string | undefined;
  const currentToken = getAccessToken();
  const alreadyRefreshed =
    !!currentToken && !!usedToken && usedToken !== currentToken;

  return new Observable<ApolloLink.Result>((observer) => {
    let closed = false;
    let retry: { unsubscribe: () => void } | undefined;

    (alreadyRefreshed
      ? Promise.resolve(currentToken)
      : refreshAccessToken()
    ).then(
      (token) => {
        if (closed) return;
        if (!token) {
          void logout();
          observer.error(error);
          return;
        }
        retry = forward(operation).subscribe({
          next: (result) => {
            if (isUnauthenticated(result.errors)) void logout();
            observer.next(result);
          },
          error: (e) => observer.error(e),
          complete: () => observer.complete(),
        });
      },
      () => {
        if (!closed) observer.error(error);
      },
    );

    return () => {
      closed = true;
      retry?.unsubscribe();
    };
  });
});

const httpLink = new HttpLink({ uri: graphqlUri, credentials: 'include' });

// Route subscriptions over SSE, everything else over HTTP.
const transportLink = split(
  ({ query }) => {
    const def = getMainDefinition(query);
    return (
      def.kind === 'OperationDefinition' && def.operation === 'subscription'
    );
  },
  new SSELink(),
  httpLink,
);

/** The full link chain around a transport; exported so tests can drive it. */
export function createApolloLink(transport: ApolloLink): ApolloLink {
  return from([errorLink, authLink, transport]);
}

export const apolloClient: ApolloClient = new ApolloClient({
  link: createApolloLink(transportLink),
  cache: new InMemoryCache({
    typePolicies: {
      // Entities are normalised by id so subscription updates (Go AGENTS.md §15.2 /
      // React AGENTS.md) patch every cached list/detail reference in place.
      Order: { keyFields: ['id'] },
      Payment: { keyFields: ['id'] },
      Refund: { keyFields: ['id'] },
      Tab: { keyFields: ['id'] },
      Terminal: { keyFields: ['id'] },
      Device: { keyFields: ['id'] },
      Product: { keyFields: ['id'] },
      Category: { keyFields: ['id'] },
      Discount: { keyFields: ['id'] },
      Table: { keyFields: ['id'] },
      LedgerTransaction: { keyFields: ['id'] },
      AdminTicket: { keyFields: ['id'] },
      TicketEmailChange: { keyFields: ['id'] },
    },
  }),
  defaultOptions: {
    watchQuery: { fetchPolicy: 'cache-and-network' },
  },
});
